import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  SupervisorAccount as SupervisorIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

const GestionTrabajadoresDialog = ({
  open,
  onClose,
  ordenId,
  ordenEstado,
  trabajadoresAsignados = [],
  serviciosActuales = [],
  onTrabajadoresUpdated
}) => {
  const [trabajadoresDisponibles, setTrabajadoresDisponibles] = useState([]);
  const [trabajadoresActuales, setTrabajadoresActuales] = useState([]);
  const [editingWorker, setEditingWorker] = useState(null);

  const [nuevoTrabajador, setNuevoTrabajador] = useState({
    id_trabajador: '',
    precio_servicio: 0, // ✅ Se calculará dinámicamente
    comision: 10,
    es_responsable: false
  });
  
  // ✅ AGREGAR: useEffect para actualizar precio automáticamente
  useEffect(() => {
    if (open) {
      const precioCalculado = calcularPrecioPorTrabajador();
      setNuevoTrabajador(prev => ({
        ...prev,
        precio_servicio: Math.round(precioCalculado * 100) / 100 // Redondear a 2 decimales
      }));
    }
  }, [open, trabajadoresActuales.length, serviciosActuales]);


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  // Estados que permiten modificación: 1=Pendiente, 2=Asignado
  const puedeModificar = [1, 2].includes(ordenEstado);

  useEffect(() => {
    if (open) {
      setTrabajadoresActuales([...trabajadoresAsignados]);
      fetchTrabajadoresDisponibles();
      setError('');
      setSuccess('');
    }
  }, [open, trabajadoresAsignados]);

  // ✅ AGREGAR: Función para calcular precio total de servicios
const calcularPrecioTotalServicios = () => {
  // Obtener servicios de la orden desde el componente padre
  return serviciosActuales?.reduce((total, servicio) => {
    return total + (servicio.cantidad * servicio.precio);
  }, 0) || 0;
};

// ✅ AGREGAR: Función para calcular precio proporcional por trabajador
const calcularPrecioPorTrabajador = () => {
  const precioTotal = calcularPrecioTotalServicios();
  const numeroTrabajadores = trabajadoresActuales.length + 1; // +1 por el nuevo trabajador
  return precioTotal / numeroTrabajadores;
};


  const fetchTrabajadoresDisponibles = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setTrabajadoresDisponibles(response.data);
    } catch (error) {
      setError('Error al cargar trabajadores disponibles');
    }
  };

  const agregarTrabajador = async () => {
    if (!nuevoTrabajador.id_trabajador || !nuevoTrabajador.precio_servicio) {
      setError('Seleccione un trabajador y especifique el precio');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/ordenes/${ordenId}/asignar-trabajador`,
        {
          idTrabajador: nuevoTrabajador.id_trabajador,
          precioServicio: nuevoTrabajador.precio_servicio,
          comision: nuevoTrabajador.comision,
          idEstadoServicio: 1,
          esResponsable: nuevoTrabajador.es_responsable || trabajadoresActuales.length === 0
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Agregar a la lista actual
      const trabajadorSeleccionado = trabajadoresDisponibles.find(
        t => t.id_trabajador === parseInt(nuevoTrabajador.id_trabajador)
      );
      
      const nuevoAsignado = {
        ...response.data,
        nombre_trabajador: trabajadorSeleccionado.nombre_trabajador,
        apellido_trabajador: trabajadorSeleccionado.apellido_trabajador,
        telefono_trabajador: trabajadorSeleccionado.telefono_trabajador,
        email_trabajador: trabajadorSeleccionado.email_trabajador,
        descripcion_estado: 'Asignado',
        es_responsable: nuevoTrabajador.es_responsable || trabajadoresActuales.length === 0
      };

      setTrabajadoresActuales([...trabajadoresActuales, nuevoAsignado]);
      
      // Reset form
      setNuevoTrabajador({
        id_trabajador: '',
        precio_servicio: 100,
        comision: 10,
        es_responsable: false
      });
      
      setSuccess('Trabajador agregado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al asignar trabajador');
    } finally {
      setLoading(false);
    }
  };

  const eliminarTrabajador = async (idAsignacion, nombreTrabajador) => {
    if (!window.confirm(`¿Eliminar a ${nombreTrabajador} del equipo?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${idAsignacion}/desasignar-trabajador`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setTrabajadoresActuales(trabajadoresActuales.filter(t => t.id_asignacion !== idAsignacion));
      setSuccess('Trabajador eliminado del equipo');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al eliminar trabajador');
    } finally {
      setLoading(false);
    }
  };

  const editarTrabajador = async (trabajadorEditado) => {
    setLoading(true);
    try {
      await axios.put(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${trabajadorEditado.id_asignacion}/editar-trabajador`,
        {
          idTrabajador: trabajadorEditado.id_trabajador,
          precioServicio: trabajadorEditado.precio_servicio,
          comision: trabajadorEditado.comision,
          idEstadoServicio: trabajadorEditado.id_estado_servicio
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Actualizar en la lista
      setTrabajadoresActuales(trabajadoresActuales.map(t => 
        t.id_asignacion === trabajadorEditado.id_asignacion ? trabajadorEditado : t
      ));
      
      setEditingWorker(null);
      setSuccess('Trabajador actualizado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al actualizar trabajador');
    } finally {
      setLoading(false);
    }
  };

  const cambiarResponsable = async (idAsignacion) => {
    setLoading(true);
    try {
      await axios.put(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${idAsignacion}/set-responsable`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Actualizar responsable en la lista local
      setTrabajadoresActuales(trabajadoresActuales.map(t => ({
        ...t,
        es_responsable: t.id_asignacion === idAsignacion
      })));
      
      setSuccess('Responsable cambiado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al cambiar responsable');
    } finally {
      setLoading(false);
    }
  };

  const intercambiarTrabajador = async (trabajadorOriginal, nuevoTrabajadorId) => {
    setLoading(true);
    try {
      // Primero eliminar el trabajador actual
      await axios.delete(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${trabajadorOriginal.id_asignacion}/desasignar-trabajador`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Luego agregar el nuevo trabajador
      const response = await axios.post(
        `${config.API_BASE_URL}/api/ordenes/${ordenId}/asignar-trabajador`,
        {
          idTrabajador: nuevoTrabajadorId,
          precioServicio: trabajadorOriginal.precio_servicio,
          comision: trabajadorOriginal.comision,
          idEstadoServicio: 1,
          esResponsable: trabajadorOriginal.es_responsable
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Actualizar en la lista
      const nuevoTrabajadorData = trabajadoresDisponibles.find(t => t.id_trabajador === parseInt(nuevoTrabajadorId));
      const trabajadorActualizado = {
        ...response.data,
        nombre_trabajador: nuevoTrabajadorData.nombre_trabajador,
        apellido_trabajador: nuevoTrabajadorData.apellido_trabajador,
        telefono_trabajador: nuevoTrabajadorData.telefono_trabajador,
        email_trabajador: nuevoTrabajadorData.email_trabajador,
        descripcion_estado: 'Asignado',
        es_responsable: trabajadorOriginal.es_responsable
      };

      setTrabajadoresActuales(trabajadoresActuales.map(t => 
        t.id_asignacion === trabajadorOriginal.id_asignacion ? trabajadorActualizado : t
      ));

      setSuccess('Trabajador intercambiado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al intercambiar trabajador');
    } finally {
      setLoading(false);
    }
  };

  const guardarCambios = () => {
    onTrabajadoresUpdated && onTrabajadoresUpdated();
    onClose();
  };

  const getNombreEstado = (estadoId) => {
    const estadosMap = {
      1: 'Pendiente',
      2: 'Asignado',
      3: 'En Camino',
      4: 'En Proceso',
      5: 'Terminado',
      6: 'En Espera',
      7: 'Cancelado'
    };
    return estadosMap[parseInt(estadoId)] || 'Desconocido';
  };

  const responsable = trabajadoresActuales.find(t => t.es_responsable);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupIcon />
          <Typography variant="h6">Gestión de Equipo de Trabajo</Typography>
          <Chip 
            label={`${trabajadoresActuales.length} miembro${trabajadoresActuales.length !== 1 ? 's' : ''}`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!puedeModificar && (
        <Alert severity="warning" sx={{ mb: 3 }}>
            Solo se pueden modificar trabajadores en estados <strong>Pendiente (1)</strong> y <strong>Asignado (2)</strong>.<br/>
            Estado actual: <strong>{getNombreEstado(ordenEstado)} ({ordenEstado})</strong>
        </Alert>
        )}
        {/* Responsable actual */}
        {responsable && (
          <Card sx={{ mb: 3, border: '2px solid #FFD700', bgcolor: 'rgba(255, 193, 7, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#FFD700', color: '#B8860B' }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#E65100' }}>
                    👑 Responsable del Servicio
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {responsable.nombre_trabajador} {responsable.apellido_trabajador}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Solo esta persona puede cambiar estados del servicio
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Lista de trabajadores actuales */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Equipo Actual
        </Typography>

        <List>
          {trabajadoresActuales.map((trabajador) => (
            <TrabajadorItem
              key={trabajador.id_asignacion}
              trabajador={trabajador}
              puedeModificar={puedeModificar}
              trabajadoresDisponibles={trabajadoresDisponibles}
              isEditing={editingWorker?.id_asignacion === trabajador.id_asignacion}
              onEdit={() => setEditingWorker(trabajador)}
              onSave={editarTrabajador}
              onCancel={() => setEditingWorker(null)}
              onDelete={eliminarTrabajador}
              onCambiarResponsable={cambiarResponsable}
              simboloMoneda={simboloMoneda}
              onIntercambiar={intercambiarTrabajador}
            />
          ))}
        </List>

        {trabajadoresActuales.length === 0 && (
          <Alert severity="info" sx={{ my: 2 }}>
            No hay trabajadores asignados a esta orden
          </Alert>
        )}

        {/* Formulario para agregar nuevo trabajador */}
        {puedeModificar && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Agregar Nuevo Trabajador
            </Typography>
            
            <Card sx={{ bgcolor: 'rgba(26, 35, 126, 0.02)' }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Seleccionar Trabajador</InputLabel>
                      <Select
                        value={nuevoTrabajador.id_trabajador}
                        onChange={(e) => setNuevoTrabajador({
                          ...nuevoTrabajador, 
                          id_trabajador: e.target.value
                        })}
                      >
                        {trabajadoresDisponibles
                          .filter(t => !trabajadoresActuales.find(ta => ta.id_trabajador === t.id_trabajador))
                          .map(trabajador => (
                            <MenuItem key={trabajador.id_trabajador} value={trabajador.id_trabajador}>
                              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Precio ${simboloMoneda}`}
                    type="number"
                    value={nuevoTrabajador.precio_servicio}
                    onChange={(e) => setNuevoTrabajador({
                      ...nuevoTrabajador, 
                      precio_servicio: parseFloat(e.target.value) || 0
                    })}
                    helperText={`Total orden: ${simboloMoneda} ${calcularPrecioTotalServicios().toFixed(2)}`} // ✅ AGREGAR
                  />
                </Grid>
                  
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Comisión %"
                      type="number"
                      value={nuevoTrabajador.comision}
                      onChange={(e) => setNuevoTrabajador({
                        ...nuevoTrabajador, 
                        comision: parseFloat(e.target.value) || 0
                      })}
                    />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={nuevoTrabajador.es_responsable}
                          onChange={(e) => setNuevoTrabajador({
                            ...nuevoTrabajador, 
                            es_responsable: e.target.checked
                          })}
                        />
                      }
                      label="Responsable"
                    />
                  </Grid>
                  
                  <Grid item xs={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={agregarTrabajador}
                      disabled={!nuevoTrabajador.id_trabajador || loading}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Agregar'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
        
      </DialogContent>
        {/* ✅ AGREGAR: Indicador de cálculo automático */}

        {nuevoTrabajador.id_trabajador && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Cálculo automático:</strong><br/>
              • Total de servicios: {simboloMoneda} {calcularPrecioTotalServicios().toFixed(2)}<br/>
              • Trabajadores: {trabajadoresActuales.length + 1}<br/>
              • Precio sugerido por trabajador: {simboloMoneda} {calcularPrecioPorTrabajador().toFixed(2)}
            </Typography>
          </Alert>
        )}
        
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button onClick={guardarCambios} variant="contained">
          Finalizar Gestión
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para cada trabajador individual
const TrabajadorItem = ({
  trabajador,
  puedeModificar,
  trabajadoresDisponibles,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onCambiarResponsable,
  onIntercambiar,
  simboloMoneda
}) => {
  const [editData, setEditData] = useState({
    precio_servicio: trabajador.precio_servicio,
    comision: trabajador.comision
  });

  useEffect(() => {
    if (isEditing) {
      setEditData({
        precio_servicio: trabajador.precio_servicio,
        comision: trabajador.comision
      });
    }
  }, [isEditing, trabajador]);

  const handleSave = () => {
    onSave({
      ...trabajador,
      ...editData
    });
  };

  if (isEditing) {
    return (
      <ListItem sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, mb: 1 }}>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: '#1A237E' }}>
            {trabajador.nombre_trabajador?.[0]}
          </Avatar>
        </ListItemAvatar>
        
        <ListItemText
          primary={
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
            </Typography>
          }
          secondary={
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label={`Precio ${simboloMoneda}`}
                  type="number"
                  value={editData.precio_servicio}
                  onChange={(e) => setEditData({
                    ...editData, 
                    precio_servicio: parseFloat(e.target.value) || 0
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Comisión %"
                  type="number"
                  value={editData.comision}
                  onChange={(e) => setEditData({
                    ...editData, 
                    comision: parseFloat(e.target.value) || 0
                  })}
                />
              </Grid>
            </Grid>
          }
        />
        
        <ListItemSecondaryAction>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Guardar cambios">
              <IconButton size="small" onClick={handleSave} color="primary">
                <SaveIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar">
              <IconButton size="small" onClick={onCancel} color="secondary">
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  return (
    <ListItem sx={{ 
      border: '1px solid rgba(26, 35, 126, 0.1)', 
      borderRadius: 1, 
      mb: 1,
      bgcolor: trabajador.es_responsable ? 'rgba(255, 193, 7, 0.1)' : 'white'
    }}>
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: '#1A237E' }}>
          {trabajador.es_responsable && (
            <StarIcon sx={{ color: '#FFD700' }} />
          )}
          {!trabajador.es_responsable && trabajador.nombre_trabajador?.[0]}
        </Avatar>
      </ListItemAvatar>
      
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
            </Typography>
            {trabajador.es_responsable && (
              <Chip 
                label="Responsable" 
                size="small" 
                color="warning"
                icon={<SupervisorIcon />}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              💰 Precio: {simboloMoneda} {trabajador.precio_servicio} | 📊 Comisión: {trabajador.comision}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              📧 {trabajador.email_trabajador} | 📱 {trabajador.telefono_trabajador}
            </Typography>
          </Box>
        }
      />
      
      {puedeModificar && (
        <ListItemSecondaryAction>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Selector para intercambiar trabajador */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => onIntercambiar(trabajador, e.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>Cambiar por...</em>
                  </MenuItem>
                  {trabajadoresDisponibles
                    .filter(t => t.id_trabajador !== trabajador.id_trabajador)
                    .map(t => (
                      <MenuItem key={t.id_trabajador} value={t.id_trabajador}>
                        {t.nombre_trabajador} {t.apellido_trabajador}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Editar datos">
                <IconButton size="small" onClick={onEdit}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              
              {!trabajador.es_responsable && (
                <Tooltip title="Hacer responsable">
                  <IconButton
                    size="small"
                    onClick={() => onCambiarResponsable(trabajador.id_asignacion)}
                    sx={{ color: '#FFD700' }}
                  >
                    <StarIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Eliminar del equipo">
                <IconButton
                  size="small"
                  onClick={() => onDelete(
                    trabajador.id_asignacion, 
                    `${trabajador.nombre_trabajador} ${trabajador.apellido_trabajador}`
                  )}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default GestionTrabajadoresDialog;